import * as React from 'react';
import {translate} from 'react-i18next';
import {ITranslatedComponent} from '../../../redux/reducers/generalReducer';
import {Table} from "semantic-ui-react";
import {compose, Dispatch} from "redux";
import {connect} from "react-redux";
import {FaqDto, ResourceListOfFaqDto} from "../../../services/Client";
import AdminViewActions from "../../../redux/actions/AdminViewActions";
import HATEOASClient from "../../../services/HATEOASClient";
import FaqHeader from "./FaqHeader";
import FaqRow from "./FaqRow";
import {
    DragDropContext,
    Draggable, DraggableProvided, DraggableStateSnapshot,
    Droppable,
    DroppableProvided,
    DroppableStateSnapshot,
    DropResult
} from "react-beautiful-dnd";
import FaqCreationDialog from "./FaqCreationDialog";
import {reorder} from "./FaqUtils";
import {selectClient} from '../../../redux/selectors';
import {IReduxState} from '../../../redux/IReduxState';
import {selectFaqs} from "../../../redux/selectors/administrationSelectors";

interface IMapDispatchToProps {
    getFaqs: () => void;
    createFaq: (links: ResourceListOfFaqDto, dto: FaqDto) => void;
    deleteFaq: (dto: FaqDto) => void;
    updateFaq: (dto: FaqDto) => void;
    moveFaq: (dto: FaqDto, toPosition: number) => void;
}

interface IMapStateToProps {
    faqs: ResourceListOfFaqDto;
    client: HATEOASClient;
}

interface IOwnProps {

}

interface FaqProps extends ITranslatedComponent, IOwnProps, IMapStateToProps, IMapDispatchToProps {
}

interface FaqState {
    editMode: boolean;
    showCreationDialog: boolean;
    filterValue?: string;
    faqs: FaqDto[];
    selectedFaqs?: FaqDto;
}

class Faq extends React.Component<FaqProps, FaqState> {
    constructor(props: FaqProps) {
        super(props);
        this.state = {
            editMode: false,
            showCreationDialog: false,
            filterValue: undefined,
            faqs: [],
            selectedFaqs: undefined,
        };
    }

    componentDidMount() {
        this.props.getFaqs();
    }

    componentDidUpdate(prevProps: Readonly<FaqProps>, prevState: Readonly<FaqState>, snapshot?: any) {
        if (prevProps.faqs !== this.props.faqs) {
            this.setState({faqs: this.props.faqs.elements ? this.props.faqs.elements : []});
        }
    }

    onDragEnd = (result: DropResult) => {
        // if not dropped outside the list and position different than initial
        if (result.destination && result.destination.index !== result.source.index) {
            const movedFaqDto = {...this.state.faqs[result.source.index]};
            const targetIndex = this.state.faqs[result.destination.index].sequence;
            if (movedFaqDto.sequence && targetIndex) {
                this.props.moveFaq(movedFaqDto, targetIndex);

                this.setState({
                    faqs: reorder(this.state.faqs, result.source.index, result.destination.index),
                });
            }
        }
    }

    onExitDialog = (faqDto?: FaqDto) => {
        if (faqDto) {
            if (this.state.selectedFaqs) {
                this.props.updateFaq({
                    ...this.state.selectedFaqs,
                    question: faqDto.question,
                    answer: faqDto.answer,
                    active: faqDto.active,
                });
            } else {
                this.props.createFaq(this.props.faqs, faqDto);
            }
        }
        this.setState({showCreationDialog: false, selectedFaqs: undefined});
    }

    onUpdateActive = (faqDto: FaqDto, active: boolean) => {
        faqDto.active = active;
        this.props.updateFaq(faqDto);
    }

    public render() {
        const {
            client,
            t,
            faqs,
            getFaqs,
            deleteFaq,
        } = this.props;

        const {
            faqs: stateFaqs,
            filterValue,
            editMode,
            selectedFaqs,
            showCreationDialog
        } = this.state;

        let filteredData = filterValue
            ? stateFaqs.filter(item => item.question.toLowerCase().includes(filterValue.toLowerCase())
                || item.answer.toLowerCase().includes(filterValue.toLowerCase()))
            : stateFaqs;

        const canEdit = filteredData.length > 0 && client.PATCH_faq(filteredData[0]).canExecute();

        return (
            // this overflow does nothing styling wise but it fixes a bug with drag-and-drop library
            // where if there are too many items and the window gets the scrollbar
            // the list breaks after a certain point.
            <div style={{overflowY: "auto", paddingBottom: 20}}>
                {showCreationDialog &&
                    <FaqCreationDialog
                        onExitDialog={this.onExitDialog}
                        editItem={selectedFaqs}
                        t={t}
                    />
                }
                <FaqHeader
                    openDialog={() => this.setState({showCreationDialog: true})}
                    onEdit={() => this.setState({editMode: !editMode})}
                    getFaqs={getFaqs}
                    onFilter={(event: React.FormEvent<{}>, newValue: string) =>
                        this.setState({filterValue: newValue !== "" ? newValue : undefined})}
                    canEdit={canEdit}
                    canCreate={client.POST_faq(faqs).canExecute()}
                    editMode={editMode}
                    filterValue={filterValue}
                    t={t}
                />

                <DragDropContext onDragEnd={this.onDragEnd}>
                    <Droppable droppableId="droppable">
                        {(provided: DroppableProvided, _: DroppableStateSnapshot) => (
                            <table
                                className='ui celled center aligned table'
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                            >
                                <Table.Header>
                                    <Table.Row>
                                        <Table.HeaderCell width='10'>
                                            {t("FAQ:QUESTION")} & {t("FAQ:ANSWER")}
                                        </Table.HeaderCell>
                                        <Table.HeaderCell width='1'>{t("FAQ:ACTIVE")}</Table.HeaderCell>
                                        <Table.HeaderCell width='1'>{t("FAQ:CREATED")}</Table.HeaderCell>
                                        <Table.HeaderCell width='1' style={{minWidth: 120}}>{t("FAQ:EDITED")}</Table.HeaderCell>
                                        <Table.HeaderCell width='2'>{t("ADMIN_VIEW:ACTIONS")}</Table.HeaderCell>
                                    </Table.Row>
                                </Table.Header>

                                <Table.Body>
                                    {filteredData && filteredData.length > 0
                                        ?
                                        filteredData.map((faq, index) =>
                                            <Draggable
                                                key={faq.objectId}
                                                draggableId={faq.objectId.toString()}
                                                index={index}
                                                isDragDisabled={!(editMode && !filterValue)}
                                            >
                                                {(provided2: DraggableProvided, snapshot2: DraggableStateSnapshot) =>
                                                    <FaqRow
                                                        provided={provided2}
                                                        snapshot={snapshot2}
                                                        key={faq.sequence}
                                                        faq={faq}
                                                        client={client}
                                                        t={t}
                                                        canEdit={editMode}
                                                        onDelete={() => deleteFaq(faq)}
                                                        onUpdate={() => this.setState({showCreationDialog: true, selectedFaqs: faq})}
                                                        onUpdateActive={this.onUpdateActive}
                                                    />
                                                }
                                            </Draggable>
                                        )
                                        :
                                        <Table.Row>
                                            <Table.Cell colSpan='5' style={{fontWeight: 700, fontSize: 30, color: '#d3d3d3'}}>
                                                {t('GLOBAL:NO_DATA')}
                                            </Table.Cell>
                                        </Table.Row>
                                    }
                                    {provided.placeholder}
                                </Table.Body>
                            </table>
                        )}
                    </Droppable>
                </DragDropContext>
            </div>
        );
    }
}

const mapStateToProps = (state: IReduxState): IMapStateToProps => ({
    faqs: selectFaqs(state),
    client: selectClient(state),
});

const mapDispatchToProps = (dispatch: Dispatch<IReduxState>, ownProps: FaqProps): IMapDispatchToProps => ({
    getFaqs: () => dispatch(AdminViewActions.GetFaqs(ownProps.t)),
    createFaq: (links: ResourceListOfFaqDto, dto: FaqDto) => dispatch(AdminViewActions.CreateFaq(ownProps.t, links, dto)),
    deleteFaq: (dto: FaqDto) => dispatch(AdminViewActions.DeleteFaq(ownProps.t, dto)),
    updateFaq: (dto: FaqDto) => dispatch(AdminViewActions.UpdateFaq(ownProps.t, dto)),
    moveFaq: (dto: FaqDto, toPosition: number) => dispatch(AdminViewActions.UpdateFaqPosition(ownProps.t, dto, toPosition)),
});

export default compose<React.ComponentType<IOwnProps>>(
    translate(["GLOBAL", "FAQ"]),
    connect<IMapStateToProps, IMapDispatchToProps>(mapStateToProps, mapDispatchToProps)
)(Faq);



// WEBPACK FOOTER //
// ./src/views/AdminView/FaqTab/Faq.tsx